<script setup>
import { ref } from 'vue';
import IconList from './iconList.vue';
import IconCard from './IconCard.vue';
import {
    CalendarIcon,
    ChevronDownIcon,
    OfficeBuildingIcon,
    CurrencyDollarIcon,
    ExclamationIcon,
    CheckCircleIcon,
    ChartBarIcon,
} from '@heroicons/vue/outline';

const props = defineProps({
    domain: {
        type: String,
        required: false,
        default: '',
    },
    report: {
        type: Object,
        required: true,
    },
    updatedAt: {
        type: [String, null],
        required: false,
        default: '-',
    },
    sectionClass: {
        type: String,
        required: false,
        default: ''
    },
    sectionContainer: {
        type: String,
        required: false,
        default: 'card-basic'
    }
})

const showShortcuts = ref(false)
const companyData = props.report;
const summaryView = ref(props.report.summary_view || false)

const anchors = [
    {
        name: 'Executive Summary',
        href: '#executive-summary',
    },
    {
        name: 'Company Overview',
        href: '#company-overview',
    },
    {
        name: 'Business Model',
        href: '#business-model',
    },
    {
        name: 'Market Position',
        href: '#market-position',
    },
    {
        name: 'Business Challenges',
        href: '#business-challenges',
    },
    {
        name: 'Strategic Initiatives',
        href: '#strategic-initiatives',
    },
    {
        name: 'Regulatory Considerations',
        href: '#regulatory-considerations',
    },
    {
        name: 'Competitive Landscape',
        href: '#competitive-landscape',
    },
    {
        name: 'Growth Indicators',
        href: '#growth-indicators',
    },
    {
        name: 'References',
        href: '#references',
    },
];

</script>

<template>
    <div class="">
        <div class="px-0 mx-auto mb-4 max-w-7xl md:px-4 lg:px-8">
            <div class="flex flex-col justify-between md:flex-row md:items-center">
                <div class="flex flex-col items-center md:flex-row gap-x-5">
                    <img :src="'https://img.logo.dev/' + props.domain.replace('https://', '').replace('http://', '') + '?token=pk_ecY6H6QYQRi7BVpPkCKfdg&size=300&retina=true'" class="w-16 h-16 mb-2 rounded-lg ring-1 ring-gray-300 md:mb-0" />

                    <div class="text-center md:text-left">
                        <h1 class="text-xl font-bold tracking-tight text-gray-900 md:text-3xl">
                            {{ companyData.company_profile.name }}
                        </h1>
                        <p class="text-xs font-medium text-gray-500">
                            {{ companyData.company_profile.industry }}
                        </p>
                    </div>
                </div>
                <div class="flex flex-col items-center mt-4 space-x-3 md:mt-0 gap-x-3">
                    <span v-if="companyData.research_confidence == 'High'" class="inline-flex items-center px-2 py-1 text-sm font-semibold text-green-800 bg-green-100 border border-green-200 rounded-full whitespace-nowrap">
                        <CheckCircleIcon class="w-4 h-4 mr-1 text-green-600" />
                        Research Confidence: {{ companyData.research_confidence }}
                    </span>
                    <span v-if="companyData.research_confidence == 'Medium'" class="inline-flex items-center px-2 py-1 text-sm font-semibold text-yellow-800 bg-yellow-100 border border-yellow-200 rounded-full whitespace-nowrap">
                        <ExclamationIcon class="w-4 h-4 mr-1 text-yellow-600" />
                        Research Confidence: {{ companyData.research_confidence }}
                    </span>
                    <span v-if="companyData.research_confidence == 'Low'" class="inline-flex items-center px-2 py-1 text-sm font-semibold text-red-800 bg-red-100 border border-red-200 rounded-full whitespace-nowrap">
                        <ExclamationIcon class="w-4 h-4 mr-1 text-red-600" />
                        Research Confidence: {{ companyData.research_confidence }}
                    </span>
                    <span class="inline-flex items-center px-3 mt-3 text-xs font-medium text-indigo-800">
                        <CalendarIcon class="w-3.5 h-3.5 mr-1" />
                        Last Updated: {{ props.updatedAt }}
                    </span>
                </div>
            </div>
        </div>
    </div>

    <div v-if="! summaryView" class="flex items-center justify-between py-2 text-center">
        <div class="px-4 mx-auto text-center max-w-7xl sm:px-6 lg:px-8">
            <div class="sticky top-0 left-0 right-0">
                <button-primary
                    class="md:hidden"
                    @click="showShortcuts = ! showShortcuts"
                >
                    Shortcuts
                    <ChevronDownIcon
                        class="w-4 h-4 ml-2 -mr-1 transition-all"
                        :class="showShortcuts ? 'rotate-180' : ''"
                    />
                </button-primary>
            </div>
            <div
                class="flex flex-col justify-center gap-2 mt-3 md:flex-wrap md:gap-1 md:mt-0"
                :class="showShortcuts ? 'block' : 'hidden md:flex md:flex-row'"
            >
                <a
                    v-for="anchor in anchors"
                    :key="anchor.href"
                    :href="anchor.href"
                    class="px-4 py-1 text-sm font-medium text-gray-500 underline rounded-md hover:text-gray-700"
                >
                    <span class="whitespace-nowrap">
                        {{ anchor.name }}
                    </span>
                </a>
            </div>
        </div>
    </div>


    <div class="py-8 -mx-6 md:mx-0">
        <div class="px-0 mx-auto space-y-12 max-w-7xl md:px-4 lg:px-8">
            <component :is="sectionContainer" :class="sectionClass" id="executive-summary">
                <div class="flex items-center mb-4 text-lg font-bold">
                    <i class="w-6 h-6 mr-2 text-indigo-600 fa-regular fa-keynote"></i>
                    <span>Executive Summary</span>
                </div>
                <p class="mb-6 leading-relaxed text-gray-700">{{ companyData.brief_summary }}</p>

                <div v-if="! summaryView">
                    <h3 class="h3">Quick Tips</h3>

                    <IconList :items="companyData.sales_tips" icon="lightbulb" iconColor="text-green-700" />
                </div>
            </component>

            <component v-if="! summaryView" :is="sectionContainer" :class="sectionClass" id="company-overview">
                <div class="flex items-center mb-4 text-lg font-bold">
                    <OfficeBuildingIcon class="w-6 h-6 mr-2 text-indigo-600" />
                    <span>Company Overview</span>
                </div>
                <p class="mb-6 leading-relaxed text-gray-700">{{ companyData.company_profile.description }}</p>

                <h3 class="h3">Company Values</h3>

                <IconList :items="companyData.company_values" icon="heart" iconColor="text-green-700" cols="3" />
            </component>

            <component v-if="! summaryView" :is="sectionContainer" :class="sectionClass" id="business-model">
                <div class="flex items-center mb-4 text-lg font-bold">
                    <CurrencyDollarIcon class="w-6 h-6 mr-2 text-indigo-600" />
                    <span>Business Model</span>
                </div>

                <h3 class="h3">Revenue Streams</h3>
                <IconList :items="companyData.business_model.revenue_streams" icon="dollar-sign" iconColor="text-green-700" cols="2" />

                <h3 class="h3">Pricing Model</h3>
                <p class="p-3 mb-6 leading-relaxed text-gray-700 bg-white rounded-md">{{ companyData.business_model.pricing_model }}</p>

                <h3 class="h3">Target Markets</h3>
                <IconList :items="companyData.business_model.target_markets" icon="globe" iconColor="text-green-700" cols="2" />

                <h3 class="h3">Distribution Channels</h3>
                <IconList :items="companyData.business_model.distribution_channels" icon="truck" type="default" cols="2" />
            </component>


            <component v-if="! summaryView" :is="sectionContainer" :class="sectionClass" id="market-position">
                <div class="flex items-center mb-4 text-lg font-bold">
                    <ChartBarIcon class="w-6 h-6 mr-2 text-indigo-600" />
                    <span>Market Position</span>
                </div>

                <div class="grid grid-cols-1 gap-8 mb-8 lg:grid-cols-2">
                    <div>
                        <h3 class="h3">Value Proposition</h3>
                        <p class="mt-2 text-gray-700">{{ companyData.market_position.value_proposition.split('[')[0] }}</p>

                        <div class="grid grid-cols-1 gap-4 mt-6 sm:grid-cols-2">
                            <div>
                                <h3 class="h3">Market Focus</h3>
                                <p class="mt-2 text-gray-700">{{ companyData.market_position.market_focus }}</p>
                            </div>
                            <div>
                                <h3 class="h3">Geographic Reach</h3>
                                <p class="mt-2 text-gray-700">{{ companyData.market_position.geographic_reach }}</p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <h3 class="h3">Unique Selling Points</h3>
                        <IconList :items="companyData.market_position.unique_selling_points" icon="star" iconColor="text-green-700" cols="1" />
                    </div>
                </div>
            </component>

            <component v-if="! summaryView" :is="sectionContainer" :class="sectionClass" id="business-challenges">
                <div class="flex items-center mb-4 text-lg font-bold">
                    <ExclamationIcon class="w-6 h-6 mr-2 text-indigo-600" />
                    <span>Business & Industry Challenges</span>
                </div>

                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div v-for="(challenge, index) in companyData.business_challenges" :key="index">
                        <IconCard icon="exclamation-triangle" type="warning">
                            <template #header>
                                <div class="flex items-center mb-4 text-lg font-bold">
                                    {{ challenge.challenge_area }}
                                </div>
                            </template>
                            <template #body>
                                <p class="mb-1 text-sm font-medium text-gray-500">Potential Impact:</p>
                                <p class="text-gray-700">{{ challenge.potential_impact.split('[')[0] }}</p>
                            </template>
                        </IconCard>
                    </div>

                </div>
            </component>

            <component v-if="! summaryView" :is="sectionContainer" :class="sectionClass" id="strategic-initiatives">
                <div class="flex items-center mb-4 text-lg font-bold">
                    <OfficeBuildingIcon class="w-6 h-6 mr-2 text-indigo-600" />
                    <span>Strategic Initiatives</span>
                </div>

                <IconList :items="companyData.strategic_initiatives" icon="rocket" type="info" cols="2" />
            </component>

            <component v-if="! summaryView" :is="sectionContainer" :class="sectionClass" id="regulatory-considerations">
                <div class="flex items-center mb-4 text-lg font-bold">
                    <OfficeBuildingIcon class="w-6 h-6 mr-2 text-indigo-600" />
                    <span>Regulatory Considerations</span>
                </div>

                <IconList :items="companyData.regulatory_considerations" icon="shield-check" type="warning" cols="2" />
            </component>

            <component v-if="! summaryView" :is="sectionContainer" :class="sectionClass" id="competitive-landscape">
                <div class="flex items-center mb-4 text-lg font-bold">
                    <OfficeBuildingIcon class="w-6 h-6 mr-2 text-indigo-600" />
                    <span>Competitive Landscape</span>
                </div>

                <div class="grid gap-8 mb-8 md:grid-cols-2 lg:grid-cols-2">
                    <div>
                        <h3 class="h3">Market Position</h3>
                        <p class="mt-2 text-gray-700">{{ companyData.competitive_landscape.market_position.split('[')[0] }}</p>
                    </div>
                    <div>
                        <h3 class="h3">Potential Competitors</h3>

                        <IconList :items="companyData.competitive_landscape.potential_competitors" icon="building" type="default" cols="1" />
                    </div>
                </div>
            </component>

            <component v-if="! summaryView" :is="sectionContainer" :class="sectionClass" id="growth-indicators">
                <div class="flex items-center mb-4 text-lg font-bold">
                    <OfficeBuildingIcon class="w-6 h-6 mr-2 text-indigo-600" />
                    <span>Growth Indicators</span>
                </div>

                <IconList :items="companyData.growth_indicators" icon="arrow-up" type="success" cols="2" />
            </component>

            <component v-if="summaryView" :is="sectionContainer" :class="sectionClass">
                <div class="flex flex-col items-center">
                    <h3>This is just a short summary</h3>
                    <p>Access the entire report, with sales tips and company analysis by upgrading to <strong>myflow Pro</strong></p>
                    <div>
                        <a :href="route('settings.company.settings')" class="w-full mt-4 text-center" target="_blank">
                            <button-success class="w-full mt-4 text-center">
                                Pricing and upgrade options
                            </button-success>
                        </a>
                    </div>
                </div>
            </component>

        </div>
    </div>
</template>

<style>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-move {
    transition: transform 0.3s ease;
}
</style>
